import { createContext, useContext } from "react";
import { setEditing } from "src/redux/slices/entrySlice";
import { store } from "src/redux/store";
import { v7 as uuidv7 } from "uuid";
import { type StateCreator, useStore } from "zustand";
import { createStore, type StoreApi } from "zustand/vanilla";

import type { V2ContentNode, V2Group, V2LogbookDef } from "./types";
import { isV2Group } from "./types";
import type { PanelEntry } from "./utils/extractPanelEntries";
import { assignIds } from "./utils/generateIds";

interface ValuesState {
  values: Record<string, any>;
  setValue: (itemId: string, value: any) => void;
  setValues: (values: Record<string, any>) => void;
}

interface LogbookState {
  entryData: V2LogbookDef | null;
  refData: V2LogbookDef | null;

  entryInfo: any;
  setEntryInfo: (info: any) => void;

  hydrate: (logbook: V2LogbookDef, newEntryInfo?: any) => void;
  reset: () => void;

  addRow: (groupId: string, templateContext: V2Group) => void;
  copyRow: (groupId: string, rowIdx: number) => void;
  deleteRow: (groupId: string, rowIdx: number) => void;
  moveRow: (groupId: string, fromIdx: number, toIdx: number) => void;
}

interface UiState {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  isDirty: boolean;
  setDirty: (dirty: boolean) => void;
  isSaving: boolean;
  panelEntries: PanelEntry[];
  setPanelEntries: (entries: PanelEntry[]) => void;
}

interface EventState {
  lastRowAction: {
    action: { type: string; itemId: string; item: any };
    timestamp: number;
  } | null;
  dispatchRowAction: (action: {
    type: string;
    itemId: string;
    item: any;
  }) => void;
}

export type GeneratorStoreState = ValuesState &
  LogbookState &
  UiState &
  EventState;

type GeneratorStoreApi = StoreApi<GeneratorStoreState>;

interface PanelNavigationState {
  activeSection: string | null;
  setActiveSection: (id: string | null) => void;
  clearActiveSection: () => void;
}

const panelNavigationStore = createStore<PanelNavigationState>(set => ({
  activeSection: null,
  setActiveSection: activeSection => set(() => ({ activeSection })),
  clearActiveSection: () => set(() => ({ activeSection: null })),
}));

function deepClone<T>(input: T): T {
  return structuredClone(input);
}

function collectInitialValues(logbook: V2LogbookDef): Record<string, any> {
  const values: Record<string, any> = {};

  function walk(nodes: V2ContentNode[]) {
    for (const node of nodes) {
      if (isV2Group(node)) {
        walk(node.content ?? []);
        continue;
      }

      if (node.id) {
        values[node.id] = node.value ?? null;
      }
    }
  }

  walk(logbook.content ?? []);
  return values;
}

function getBaseGroupId(groupStr: string): string {
  return groupStr.replace(
    /-[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
    "",
  );
}

function sanitizeRowGroup(group: V2Group): V2Group {
  const cloned = deepClone(group);

  const cleanGroup = (g: V2Group): V2Group => {
    const baseGroup = getBaseGroupId(g.group);
    /* eslint-disable @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose */
    const { addRow, copyRow, moveRow, deleteRow, ...groupWithoutRowControls } =
      g;
    /* eslint-enable @typescript-eslint/no-unused-vars */

    const gCopy: V2Group = {
      ...groupWithoutRowControls,
      group: `${baseGroup}-${uuidv7()}`,
      rowInstance: true,
    };

    if (gCopy.content) {
      gCopy.content = gCopy.content.map((child: V2ContentNode) => {
        if (isV2Group(child)) {
          return cleanGroup(child);
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
        const { id, _path, ...restChild } = child as any;
        return restChild as V2ContentNode;
      });
      // Regenerate IDs and paths for the row content
      const { content } = assignIds(gCopy.content);
      gCopy.content = content;
    }

    return gCopy;
  };

  return cleanGroup(cloned);
}

function updateLogbookTree(
  logbook: V2LogbookDef | null,
  groupId: string,
  updater: (group: V2Group) => V2ContentNode[],
): V2LogbookDef | null {
  if (!logbook) return null;

  function walkTree(nodes: V2ContentNode[]): V2ContentNode[] {
    return nodes.map(node => {
      if (isV2Group(node)) {
        if (node.group === groupId || node.id === groupId) {
          return { ...node, content: updater(node) };
        }
        if (node.content) {
          return { ...node, content: walkTree(node.content) };
        }
      }
      return node;
    });
  }

  return {
    ...logbook,
    content: walkTree(logbook.content),
  };
}

let editingTimeout: NodeJS.Timeout | null = null;

export function markEditing() {
  if (!store.getState().entrySlice.editing) {
    store.dispatch(setEditing(true));
  }

  if (editingTimeout) {
    clearTimeout(editingTimeout);
  }

  editingTimeout = setTimeout(() => {
    store.dispatch(setEditing(false));
    editingTimeout = null;
  }, 5000);
}

const createGeneratorStoreState: StateCreator<GeneratorStoreState> = (
  set,
  get,
) => ({
  values: {},
  setValue: (itemId: string, value: any) => {
    markEditing();
    set(state => ({
      values: { ...state.values, [itemId]: value },
      isDirty: true,
    }));
  },
  setValues: (newValues: Record<string, any>) => {
    markEditing();
    set(state => ({
      values: { ...state.values, ...newValues },
      isDirty: true,
    }));
  },

  entryData: null,
  refData: null,

  entryInfo: null,
  setEntryInfo: (info: any) => set(() => ({ entryInfo: info })),

  hydrate: (logbook: V2LogbookDef) => {
    const entryData = deepClone(logbook);
    const refDataBase = deepClone(logbook);
    const prepared = assignIds(refDataBase.content);
    const refData = { ...refDataBase, content: prepared.content };
    const values = collectInitialValues(refData);

    set(() => {
      return {
        entryData,
        refData,
        values,
        isDirty: false,
        isLoading: false,
      };
    });
  },

  reset: () => {
    const state = get();
    if (!state.entryData) return;
    get().hydrate(state.entryData, state.entryInfo);
  },

  addRow: (groupId: string, templateContext: V2Group) => {
    markEditing();
    set(state => {
      const newRow = sanitizeRowGroup(templateContext);
      const updated = updateLogbookTree(state.refData, groupId, group => {
        const currentRows = group.content?.filter(isV2Group) ?? [];
        return [...currentRows, newRow];
      });

      return {
        refData: updated,
        isDirty: true,
      };
    });
  },

  copyRow: (groupId: string, rowIdx: number) => {
    markEditing();
    set(state => {
      const valuesToUpdate: Record<string, any> = {};

      const newLogbook = updateLogbookTree(state.refData, groupId, group => {
        const rows = group.content?.filter(isV2Group) ?? [];
        if (rowIdx < 0 || rowIdx >= rows.length) return group.content ?? [];

        const sourceRow = rows[rowIdx];
        const newRow = sanitizeRowGroup(sourceRow);

        const mapIds = (oldNode: V2ContentNode, newNode: V2ContentNode) => {
          if (isV2Group(oldNode) && isV2Group(newNode)) {
            const oldChildren = oldNode.content ?? [];
            const newChildren = newNode.content ?? [];
            for (
              let i = 0;
              i < Math.min(oldChildren.length, newChildren.length);
              i++
            ) {
              mapIds(oldChildren[i], newChildren[i]);
            }
          } else if (!isV2Group(oldNode) && !isV2Group(newNode)) {
            if (oldNode.id && newNode.id) {
              const val = state.values[oldNode.id];
              if (val !== undefined) {
                valuesToUpdate[newNode.id] = val;
              }
            }
          }
        };

        mapIds(sourceRow, newRow);
        return [...rows, newRow];
      });

      const nextValues =
        Object.keys(valuesToUpdate).length > 0
          ? { ...state.values, ...valuesToUpdate }
          : state.values;

      return {
        values: nextValues,
        refData: newLogbook,
        isDirty: true,
      };
    });
  },

  deleteRow: (groupId: string, rowIdx: number) => {
    markEditing();
    set(state => {
      const updated = updateLogbookTree(state.refData, groupId, group => {
        const rows = group.content?.filter(isV2Group) ?? [];
        if (rows.length <= 1) return group.content ?? [];
        const nonRows = group.content?.filter(c => !isV2Group(c)) ?? [];
        return [
          ...nonRows,
          ...rows.slice(0, rowIdx),
          ...rows.slice(rowIdx + 1),
        ];
      });

      return {
        refData: updated,
        isDirty: true,
      };
    });
  },

  moveRow: (groupId: string, fromIdx: number, toIdx: number) => {
    markEditing();
    set(state => {
      const updated = updateLogbookTree(state.refData, groupId, group => {
        const rows = group.content?.filter(isV2Group) ?? [];
        if (fromIdx < 0 || fromIdx >= rows.length) return group.content ?? [];
        if (toIdx < 0 || toIdx >= rows.length) return group.content ?? [];

        const newRows = [...rows];
        const [moved] = newRows.splice(fromIdx, 1);
        newRows.splice(toIdx, 0, moved);
        return newRows;
      });

      return {
        refData: updated,
        isDirty: true,
      };
    });
  },

  isLoading: false,
  setLoading: loading => set(() => ({ isLoading: loading })),
  isDirty: false,
  setDirty: dirty => set(() => ({ isDirty: dirty })),
  isSaving: false,

  panelEntries: [],
  setPanelEntries: panelEntries => set(() => ({ panelEntries })),

  lastRowAction: null,
  dispatchRowAction: action =>
    set(() => ({ lastRowAction: { action, timestamp: Date.now() } })),
});

export const generatorStore = createStore<GeneratorStoreState>(
  createGeneratorStoreState,
);

export const useGeneratorStore = <T>(
  selector: (state: GeneratorStoreState) => T,
) => useStore(generatorStore, selector);

export const createGeneratorStore = () =>
  createStore<GeneratorStoreState>(createGeneratorStoreState);

const GeneratorStoreContext = createContext<GeneratorStoreApi | null>(null);

export const GeneratorStoreProvider = GeneratorStoreContext.Provider;

export const useScopedGeneratorStore = <T>(
  selector: (state: GeneratorStoreState) => T,
) => {
  const scopedStore = useContext(GeneratorStoreContext);
  return useStore(scopedStore ?? generatorStore, selector);
};

export const usePanelNavigationStore = <T>(
  selector: (state: PanelNavigationState) => T,
) => useStore(panelNavigationStore, selector);

export const setSharedActiveSection = (activeSection: string | null) =>
  panelNavigationStore.getState().setActiveSection(activeSection);

export const clearSharedActiveSection = () =>
  panelNavigationStore.getState().clearActiveSection();
